/**
 * @module composables/blockGuard
 * @description Output Guard L2 (FE 레이어, AA-48) — ai-service가 보낸 구조화 블록 payload를
 * 렌더 파이프라인에 넣기 전에 형식을 검증한다. 구조적으로 깨진 블록을 차단하여 패널 안정성을 보장한다.
 * (각 블록 컴포넌트의 element-level 정규화와 2중 방어 — 여기서는 구조 검증만)
 * ⚠️ PII 마스킹은 ai-service(BE) 책임이며 본 모듈 범위가 아니다.
 */

import { NAV_ENTITIES } from '@/components/assistant/navRegistry'

const CHART_TYPES = ['bar', 'line', 'pie']

/**
 * 차트 블록 payload 구조 검증. chartType은 선택(미지정 시 기본 bar)이며, 지정 시 허용값만.
 * series 원소의 element-level 검증은 ChartBlock이 수행하므로 여기서는 배열 여부만 본다.
 * @param {object} d
 * @returns {boolean}
 */
function isValidChart(d) {
  if (!d || !Array.isArray(d.labels) || !Array.isArray(d.series)) return false
  if (d.chartType && !CHART_TYPES.includes(d.chartType)) return false
  return true
}

const VALIDATORS = {
  text: (d) => !!d && typeof d.markdown === 'string',
  table: (d) => !!d && Array.isArray(d.columns) && Array.isArray(d.rows),
  chart: isValidChart,
  card: (d) => !!d && (typeof d.title === 'string' || Array.isArray(d.fields)),
  // link: 외부 url 또는 내부 entity(navRegistry 허용목록) 둘 중 하나 (OD-13)
  link: (d) => !!d && (typeof d.url === 'string' || (typeof d.entity === 'string' && NAV_ENTITIES.includes(d.entity))),
  draft_confirm: (d) => !!d && typeof d === 'object',
  // consultation_draft: 상담 등록 HITL 카드(AA-117) — draft_confirm과 동일하게 구조만 확인,
  // 필드별 상세 검증은 ConsultationDraftBlock이 담당
  consultation_draft: (d) => !!d && typeof d === 'object',
  // admin_draft: 관리 설정 HITL 카드(AA-119, D10 공용) — commit 필수 필드 + preview 배열 구조 확인,
  // candidates/input_fields 상세 검증은 AdminActionDraftBlock이 담당
  admin_draft: (d) => !!(d && d.draft_id && d.confirmation_token && d.action && Array.isArray(d.preview)),
  // reply_draft: 고객 응대 초안(F2) — draft(복사 대상 본문) 필수
  reply_draft: (d) => !!d && typeof d.draft === 'string' && d.draft.trim().length > 0
}

/**
 * 블록이 렌더 가능한 유효 구조인지 검증한다.
 * 미지원 타입은 BlockRenderer의 폴백이 처리하므로 여기서 막지 않는다(통과).
 * @param {object} block - { type, data }
 * @returns {boolean}
 */
export function isValidBlock(block) {
  if (!block || !block.type) return false
  const v = VALIDATORS[block.type]
  if (!v) return true
  return v(block.data || {})
}
